import React, { useEffect } from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { CKEditor } from "ckeditor4-react";
import { Box } from "@mui/material";
import { Translations } from "ui/Scripts/source/models/translations";

interface IAppCkEditorProps {
    readOnly?: boolean;
    value?: string;
    height: string;
    dataId: number;
    htmlMode?: boolean;
    toolbar?: (string | string[])[];
    documentLandscapeOrientation?: boolean;
    fitToContainerHeight?: boolean;
    onInstanceReady: (ev: any) => void;
    onChange: (ev: any) => void;
}

export const AppCkEditor = (props: IAppCkEditorProps) => {
    const ckEditorContainerDiv = React.useRef<HTMLDivElement>(null);
    const editor = React.useRef<any>(null);
    const fitToContainerHeightRef = React.useRef(props.fitToContainerHeight);
    const [toolbarHeight, setToolbarHeight] = React.useState(0);
    const [fitToContainerHeight, setFitToContainerHeight] = React.useState(props.fitToContainerHeight);

    const setCkEditorStyles = () => {
        if (!ckEditorContainerDiv.current) return;
        Base.setCkEditorStyles(ckEditorContainerDiv.current, !props.htmlMode, !!props.documentLandscapeOrientation);
    };

    const handleCkEditorInstanceReady = (ev) => {
        editor.current = ev.editor;
        props.onInstanceReady(ev);
    };

    useEffect(() => {
        setCkEditorStyles();
    }, [props.dataId, props.documentLandscapeOrientation ?? false]);

    //For handling zoom changes and container resizes
    const handleResize = (_entries) => {
        if (!ckEditorContainerDiv.current || !fitToContainerHeight || !fitToContainerHeightRef.current) return;
        if (!editor.current) return;
        const toolbarElement = editor.current.ui.space("top"); // 'top' refers to the toolbar area
        if (!toolbarElement?.$) return;
        const rect = toolbarElement.$.getBoundingClientRect();
        if (!rect.height) return;
        const containerHeight = ckEditorContainerDiv.current.getBoundingClientRect().height;
        const newToolbarHeight = Math.ceil(rect.height);
        const editAreaHeight = Math.max(containerHeight - newToolbarHeight, 0);
        setToolbarHeight(rect.height);
        editor.current.resize("100%", editAreaHeight.toString(), true);
    };

    useEffect(() => {
        if (!ckEditorContainerDiv.current || !fitToContainerHeight) return;

        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(ckEditorContainerDiv.current);
        
        return () => {
            if (ckEditorContainerDiv.current) {
                resizeObserver.unobserve(ckEditorContainerDiv.current);
            } else {
                resizeObserver.disconnect();
            }
        };
    }, [ckEditorContainerDiv.current]);

    const handleBeforeLoad = (CKEDITOR) => {
        CKEDITOR.on("dialogDefinition", (evt) => {
            const dialogName = evt.data.name;
            const dialogDefinition = evt.data.definition;
            // Target the base64image dialog
            if (dialogName === "base64imageDialog") {
                const sourceTab = dialogDefinition.getContents("tab-source");
                const base64imageDialogInfoId = "base64imageCustomMessageDiv";
                if (sourceTab.get(base64imageDialogInfoId)) return; // Avoid adding the message multiple times
                sourceTab.add({
                    type: "html",
                    id: base64imageDialogInfoId,
                    html: `
                        <div id="my-custom-container" style="padding: 10px; background-color: #f9f9f9; border: 1px solid #ddd; margin-top: 10px;">
                            <strong>${Translations.Attention}</strong>
                            <p>${String.format(Translations.MaximumSizeOfImageIsParameterMB, appConfig.maxImageSizeInMegabytes)}</p>
                        </div>
                    `
                });
            }
        });
    };

    return (
        <Box
            ref={ckEditorContainerDiv}
            sx={{
                "height": fitToContainerHeight ? "100%" : undefined,
                "& div.cke": {
                    "height": "100%",
                    "& div.cke_inner": {
                        height: "100%"
                    }
                }
            }}
        >
            <CKEditor
                config={{
                    extraPlugins: "base64image,base64imagepaste",
                    removePlugins: "image,elementspath",
                    language: Base.getCultureSimple(appConfig.culture),
                    toolbar: props.toolbar ?? [
                        ["Cut", "Copy", "Paste", "PasteText", "PasteFromWord", "-", "Undo", "Redo"],
                        ["Find", "Replace"],
                        ["Link", "Unlink"],
                        ["Table", "Smiley"],
                        ["TextColor", "BGColor", "Maximize"],
                        ["base64image"],
                        "/",
                        ["Format", "Font", "FontSize"],
                        ["Bold", "Italic", "Underline", "Strike", "Subscript", "Superscript", "RemoveFormat"],
                        ["JustifyLeft", "JustifyCenter", "JustifyRight", "JustifyBlock"],
                        ["NumberedList", "BulletedList", "Outdent", "Indent", "PageBreak"]
                    ],
                    font_defaultLabel: "Arial",
                    fontSize_defaultLabel: "14",
                    resize_enabled: false,
                    height: !fitToContainerHeight
                        ? props.height
                        : (!toolbarHeight
                            ? "calc(100% - 75px)"
                            : `calc(100% - ${toolbarHeight}px)`),
                    readOnly: props.readOnly
                }}
                initData={props.value ?? ""}
                onBeforeLoad={handleBeforeLoad}
                onInstanceReady={handleCkEditorInstanceReady}
                editorUrl={appConfig.rootUrl + "Scripts/ckeditor/ckeditor.js"}
                onChange={props.onChange}
                onAfterCommandExec={(ev) => {
                    //Disable height fitting when maximized
                    if (ev.data.name === "maximize" && (ev.data.command as any).state === 1) {
                        fitToContainerHeightRef.current = false;
                        setFitToContainerHeight(false);
                    } else {
                        fitToContainerHeightRef.current = props.fitToContainerHeight;
                        setFitToContainerHeight(props.fitToContainerHeight);
                    }
                }}
            />
        </Box>
    );
};
